import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import RefreshIcon from '@mui/icons-material/Refresh'
import IconButton from '@mui/material/IconButton'
import {
  loadRecommendationsRoutine,
  dismissRecommendationRoutine,
} from 'ducks/guruRecommendations/actions'
import { selectRecommendationsFor } from 'ducks/guruRecommendations/selectors'
import { openWithEntity } from 'ducks/guruWidget/actions'
import RecommendationCard from './RecommendationCard'
import { useGuruScope } from './hooks/useGuruScope'
import { handleCta } from './guruCtaRegistry'
import { quickPromptsFor, subSurfaceLabel } from './subSurfacePrompts'
import { readGuruPreferences } from './guruPreferences'
import { getGuruCapabilities } from 'services/GuruChatService'

const VISIBLE_LIMIT = 6
const DISMISS_ANIMATION_MS = 220

const scopeMatches = (categoryScope, contextType) => {
  if (categoryScope === 'any') return true
  if (!contextType || contextType === 'user') return true
  return categoryScope === contextType
}

const CardSlot = styled.div`
  overflow: hidden;
  opacity: ${({ $closing }) => ($closing ? 0 : 1)};
  max-height: ${({ $closing }) => ($closing ? '0px' : '200px')};
  margin-bottom: ${({ $closing }) => ($closing ? '0px' : '0')};
  transform: ${({ $closing }) => ($closing ? 'translateX(8px)' : 'translateX(0)')};
  transition: opacity ${DISMISS_ANIMATION_MS}ms ease,
              max-height ${DISMISS_ANIMATION_MS}ms ease,
              transform ${DISMISS_ANIMATION_MS}ms ease;
`

const Wrap = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px 8px;
`

const ContextRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const QuickPromptsSection = styled.div`
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.primary}08;
  border: 1px solid ${({ theme }) => theme.colors.primary}22;
`

const QuickPromptsHeading = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 6px;
`

const QuickPromptList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const CapabilitiesSection = styled.div`
  margin-bottom: 14px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  overflow: hidden;
`

const CapabilitiesToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.backgroundColor || '#fafafa'};
  border: none;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
`

const CapabilitiesBody = styled.div`
  padding: 8px 12px 12px;
`

const CapabilityGroup = styled.div`
  margin-top: 10px;

  &:first-child { margin-top: 4px; }
`

const CapabilityGroupTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-bottom: 6px;
`

const CapabilityList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const QuickPromptChip = styled.button`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 14px;
  padding: 5px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  line-height: 1.2;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }
`

const Skeleton = styled.div`
  height: 86px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f5f5f5 25%, #ececec 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
  margin-bottom: 10px;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`

const EmptyState = styled.div`
  padding: 24px 8px 16px;
  text-align: center;
`

const EmptyHeadline = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 500;
  margin-bottom: 4px;
`

const EmptyHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-bottom: 14px;
`

const PromptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`

const PromptChip = styled.button`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 18px;
  padding: 8px 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }
`

const ShowMoreBtn = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 0;
  margin-top: 4px;
  align-self: center;

  &:hover { text-decoration: underline; }
`

const GuruSuggestions = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { scope: ctx } = useGuruScope()
  const recommendationsSelector = useMemo(
    () => selectRecommendationsFor(ctx.contextType, ctx.contextId),
    [ctx.contextType, ctx.contextId],
  )
  const slice = useSelector(recommendationsSelector)
  const [showAll, setShowAll] = useState(false)
  const [closingSignatures, setClosingSignatures] = useState(() => new Set())
  const [capabilities, setCapabilities] = useState([])
  const [showCapabilities, setShowCapabilities] = useState(false)

  useEffect(() => {
    let cancelled = false
    getGuruCapabilities()
      .then(res => { if (!cancelled) setCapabilities(res?.data?.data || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!ctx.contextType) return
    if (ctx.contextType !== 'user' && !ctx.contextId) return
    dispatch(loadRecommendationsRoutine({
      contextType: ctx.contextType,
      contextId: ctx.contextId,
      surface: 'widget',
      subSurface: ctx.subSurface,
      entityType: ctx.entityType,
      entityId: ctx.entityId,
    }))
  }, [ctx.contextType, ctx.contextId, ctx.subSurface, ctx.entityType, ctx.entityId, dispatch])

  const isLoading = slice.loading
  const prefs = readGuruPreferences()
  const cards = prefs.showInfoCards
    ? slice.items
    : slice.items.filter(c => c.tier !== 'info')

  const visibleCards = showAll ? cards : cards.slice(0, VISIBLE_LIMIT)
  const hiddenCount = cards.length - visibleCards.length

  const quickPrompts = quickPromptsFor(ctx)
  const surfaceLabel = subSurfaceLabel(ctx.subSurface)
  const emptyPrompts = quickPrompts.length ? quickPrompts.slice(0, 3) : []
  const scopedCapabilities = capabilities.filter(c => scopeMatches(c.scope, ctx.contextType))

  const handleRefresh = () => {
    if (!ctx.contextType || !ctx.contextId) return
    dispatch(loadRecommendationsRoutine({
      contextType: ctx.contextType,
      contextId: ctx.contextId,
      surface: 'widget',
      subSurface: ctx.subSurface,
      entityType: ctx.entityType,
      entityId: ctx.entityId,
    }))
  }

  const handlePrompt = prompt => dispatch(openWithEntity({
    anchorContext: ctx,
    initialView: 'chat',
    prefilledInput: prompt,
  }))

  const onCta = card => handleCta(card, ctx, { dispatch, navigate })
  const onDismiss = (card, scope) => {
    setClosingSignatures(prev => {
      const next = new Set(prev)
      next.add(card.signature)
      return next
    })
    setTimeout(() => {
      dispatch(dismissRecommendationRoutine({
        signature: card.signature,
        scope,
        contextType: ctx.contextType,
        contextId: ctx.contextId,
      }))
    }, DISMISS_ANIMATION_MS)
  }

  return (
    <Wrap>
      {(ctx.contextLabel || ctx.contextType) && (
        <ContextRow>
          <span>
            {ctx.contextLabel ? `For: ${ctx.contextLabel}` : 'Suggestions for you'}
            {surfaceLabel && <> · {surfaceLabel}</>}
          </span>
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh suggestions"
            sx={{ padding: '2px' }}
          >
            <RefreshIcon style={{ fontSize: 16 }} />
          </IconButton>
        </ContextRow>
      )}

      {quickPrompts.length > 0 && (
        <QuickPromptsSection>
          <QuickPromptsHeading>
            {surfaceLabel ? `Quick prompts · ${surfaceLabel}` : 'Quick prompts'}
          </QuickPromptsHeading>
          <QuickPromptList>
            {quickPrompts.map(p => (
              <QuickPromptChip key={p} onClick={() => handlePrompt(p)}>{p}</QuickPromptChip>
            ))}
          </QuickPromptList>
        </QuickPromptsSection>
      )}

      {scopedCapabilities.length > 0 && (
        <CapabilitiesSection>
          <CapabilitiesToggle
            onClick={() => setShowCapabilities(v => !v)}
            aria-expanded={showCapabilities}
          >
            <span>Things I can do for you</span>
            <span>{showCapabilities ? '−' : '+'}</span>
          </CapabilitiesToggle>
          {showCapabilities && (
            <CapabilitiesBody>
              {scopedCapabilities.map(category => (
                <CapabilityGroup key={category.title}>
                  <CapabilityGroupTitle>{category.title}</CapabilityGroupTitle>
                  <CapabilityList>
                    {category.items.map(item => (
                      <QuickPromptChip
                        key={item.action}
                        title={item.example}
                        onClick={() => handlePrompt(item.example)}
                      >
                        {item.label}
                      </QuickPromptChip>
                    ))}
                  </CapabilityList>
                </CapabilityGroup>
              ))}
            </CapabilitiesBody>
          )}
        </CapabilitiesSection>
      )}

      {isLoading && cards.length === 0 && (
        <>
          <Skeleton /><Skeleton /><Skeleton />
        </>
      )}

      {!isLoading && cards.length === 0 && (
        <EmptyState>
          <EmptyHeadline>You&apos;re all caught up</EmptyHeadline>
          <EmptyHint>Nothing urgent right now. Try one of the prompts above, or ask:</EmptyHint>
          <PromptList>
            {emptyPrompts.map(p => (
              <PromptChip key={p} onClick={() => handlePrompt(p)}>{p}</PromptChip>
            ))}
          </PromptList>
        </EmptyState>
      )}

      {visibleCards.map(card => (
        <CardSlot key={card.signature} $closing={closingSignatures.has(card.signature)}>
          <RecommendationCard
            card={{
              tier: card.tier,
              title: card.title,
              subtitle: card.subtitle,
              ctaLabel: card.ctaLabel,
              ctaKind: card.ctaKind,
              ctaPayload: card.ctaPayload || {},
              signature: card.signature,
            }}
            onCtaClick={onCta}
            onDismiss={scope => onDismiss(card, scope)}
          />
        </CardSlot>
      ))}

      {!showAll && hiddenCount > 0 && (
        <ShowMoreBtn onClick={() => setShowAll(true)}>
          Show {hiddenCount} more
        </ShowMoreBtn>
      )}
    </Wrap>
  )
}

export default GuruSuggestions
